import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

enum PromiseSetState {
  Pending,
  Resolved,
  Rejected,
  VoidResolved,
}
class ResolveRejectEntry {
  constructor(public resolve: Function, public reject: Function) {}
}

// continue to add promises after pending to promise set
const TRACE = false;
const TRACE_TIMEOUT = 120000;

/**
 * Please consider alternatives
 */
export class PromiseSet<T> {
  protected _promiseSet = new Set<ResolveRejectEntry>();
  protected _state: PromiseSetState = PromiseSetState.Pending;

  // if you wish to access internal value, use get().then() as per promise
  protected value: boolean | T | PromiseLike<T>;

  protected static ResolveRejectEntry: typeof ResolveRejectEntry = ResolveRejectEntry;

  constructor() {
    if (TRACE) {
      this["timeoutError"] = [];
      this["timeoutId"] = setTimeout(() => {
        if (this.isPending) {
          logger.out.error(`PromiseSet timeout` + this["timeoutError"].length);
          this["timeoutError"].forEach((e) => {
            logger.out.error(e.stack);
          });

          this.reject(new Error("PromiseSet error"));
        }
      }, TRACE_TIMEOUT);
    }
  }

  public isPending(): boolean {
    return this._state === PromiseSetState.Pending;
  }

  public isResolved(): boolean {
    return this._state === PromiseSetState.VoidResolved || this._state === PromiseSetState.Resolved;
  }

  public isRejected(): boolean {
    return this._state === PromiseSetState.Rejected;
  }

  public get<T>(): Promise<T> {
    let isReadyPromise: Promise<T>;

    if (TRACE) this["timeoutError"].push(new Error());

    switch (this._state) {
      case PromiseSetState.Pending:
        // pending cubiq broker connection
        isReadyPromise = new Promise<T>((resolve, reject) => {
          // always needed for Pending.
          this._promiseSet.add(new ResolveRejectEntry(resolve, reject));
        });

        break;
      case PromiseSetState.Resolved:
        // already ready keep track of set for debug
        isReadyPromise = new Promise<T>((resolve, reject) => {
          if (TRACE) this._promiseSet.add(new ResolveRejectEntry(resolve, reject));

          resolve(this.value as any);
        });

        break;
      case PromiseSetState.Rejected:
        isReadyPromise = new Promise<T>((resolve, reject) => {
          if (TRACE) this._promiseSet.add(new ResolveRejectEntry(resolve, reject));

          reject(this.value);
        });

        break;
      case PromiseSetState.VoidResolved:
        isReadyPromise = new Promise<void>((resolve, reject) => {
          if (TRACE) this._promiseSet.add(new ResolveRejectEntry(resolve, reject));

          resolve();
        }) as unknown as any;

        break;
      default:
        // this should not be reachable
        isReadyPromise = new Promise<T>((resolve, reject) => {
          if (TRACE) this._promiseSet.add(new ResolveRejectEntry(resolve, reject));
          console.debug(`PromiseSet in unknown state ${this.value}`);
          reject(Error(`PromiseSet in unknown state ${this.value}`));
        });

        break;
    }

    return isReadyPromise;
  }

  public reject(reason?: any) {
    if (this._state !== PromiseSetState.Pending) {
      console.debug("PromiseSet: subsequent reject called has been ignored as per Promise spec");

      return;
    }
    if (TRACE) clearTimeout(this["timeoutId"]);
    this.value = reason;
    let entries: Array<ResolveRejectEntry> = Array.from(this._promiseSet.values());

    for (let i = 0; i < entries.length; i++) {
      entries[i].reject(reason);
    }

    this._state = PromiseSetState.Rejected;
  }

  public resolve(value?: T | PromiseLike<T>);
  public resolve(...args: any[]) {
    if (this._state !== PromiseSetState.Pending) {
      console.debug("PromiseSet: subsequent resolve called has been ignored as per Promise spec");

      return;
    }
    if (TRACE) clearTimeout(this["timeoutId"]);
    let entries: Array<ResolveRejectEntry> = Array.from(this._promiseSet.values());

    if (args.length === 0) {
      for (let i = 0; i < entries.length; i++) {
        entries[i].resolve();
      }

      this._state = PromiseSetState.VoidResolved;
    } else {
      this.value = args[0];

      for (let i = 0; i < entries.length; i++) {
        entries[i].resolve(this.value);
      }

      this._state = PromiseSetState.Resolved;
    }
  }
}
